package at.apa.statistictracker.data.local

import at.apa.statistictracker.data.model.StatisticPayload
import at.apa.statistictracker.db.StatisticPayloadTable
import at.apa.statistictracker.db.StatisticTrackerDb
import at.apa.statistictracker.logger.Logger
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

internal class StatisticPayloadDao(database: StatisticTrackerDb) {

    private val statisticPayloadQueries = database.statisticPayloadQueries

    /**
     * Loads all statistic payloads from the database.
     */
    suspend fun getAllStatisticPayloads(): List<StatisticPayload> =
        withContext(Dispatchers.Default) {
            statisticPayloadQueries
                .selectAll()
                .executeAsList()
                .map { statisticPayloadMapper(it) }
        }

    /**
     * Inserts a statistic payload into the database.
     */
    suspend fun insert(statisticPayload: StatisticPayload): Long =
        withContext(Dispatchers.Default) {
            statisticPayloadQueries.transactionWithResult {
                Logger.d(TAG, "insert(statisticPayload: $statisticPayload)")

                statisticPayloadQueries
                    .insert(statisticPayload.payload, statisticPayload.timestampInUnixMillis)

                val id = statisticPayloadQueries.lastInsertedId().executeAsOne()
                Logger.d(TAG, "insert() created statistic payload $statisticPayload with id $id")

                id
            }
        }

    /**
     * Loads the last [n] oldest statistic payloads from the database.
     *
     * @param n Limits the number of statistic payloads.
     */
    suspend fun getNOldestStatisticPayloads(n: Int): List<StatisticPayload> =
        withContext(Dispatchers.Default) {
            Logger.d(TAG, "getNOldestStatisticPayloads(n: $n)")

            statisticPayloadQueries
                .selectNOldest(n.toLong())
                .executeAsList()
                .map { statisticPayloadMapper(it) }
                .also {
                    Logger.d(TAG, "getNOldestStatisticPayloads() fetched $it")
                }
        }


    /**
     * Deletes statistic payloads from the database.
     *
     * @param ids the statistic payload ids to be deleted.
     */
    suspend fun deleteIds(vararg ids: Long) {
        withContext(Dispatchers.Default) {
            Logger.d(TAG, "deleteIds(ids: ${ids.contentToString()})")

            statisticPayloadQueries.deleteIds(ids.asList())
        }
    }

    private fun statisticPayloadMapper(statisticPayloadTable: StatisticPayloadTable): StatisticPayload =
        StatisticPayload(
            statisticPayloadTable.id,
            statisticPayloadTable.payload,
            statisticPayloadTable.timestampUnixMillis
        )

    companion object {
        private const val TAG = "StatisticPayloadDao"
    }
}

